// SALUD DEL NÚMERO — score 0-100 + semáforo (portado de la agenda dental, 12-oct-2026).
// Lee la foto diaria de /api/messages/whatsapp/health. Responde a la dueña: «¿está sano
// el número del estudio?». Colores con clases Tailwind (este front no tiene --success/--warning).
import { useState, type ReactNode } from 'react';
import {
  HeartPulse, MessageCircleReply, MessagesSquare, BellOff, CalendarClock, ShieldCheck, Users,
  Snowflake, AlertTriangle, Shield, ShieldAlert, HelpCircle,
} from 'lucide-react';
import type { WaHealth } from '../../services/whatsappApi';

const cardCls = 'rounded-2xl border border-line bg-surface p-5 shadow-sm';

const fechaCorta = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });

const SEMAFORO = {
  verde: { label: 'Saludable', pill: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300', bar: 'bg-emerald-500' },
  ambar: { label: 'Precaución', pill: 'bg-amber-500/15 text-amber-700 dark:text-amber-300', bar: 'bg-amber-500' },
  rojo:  { label: 'En riesgo', pill: 'bg-red-500/15 text-red-700 dark:text-red-300', bar: 'bg-red-500' },
} as const;

const pct = (r: number | null | undefined) => (r === null || r === undefined) ? null : `${Math.round(r * 100)}%`;

type Row = { icon: ReactNode; label: string; value: string; tip?: string };

export default function WhatsAppHealthCard({ health }: { health: WaHealth }) {
  const [showHelp, setShowHelp] = useState(false);
  const [openTip, setOpenTip] = useState<number | null>(null);

  if (!health.available) {
    return (
      <div className={`mt-5 ${cardCls}`} data-testid="wa-health">
        <h2 className="flex items-center gap-2 font-display text-base font-bold text-content">
          <HeartPulse size={18} className="text-emerald-500" /> Salud del número
        </h2>
        <p className="mt-1 text-sm text-content-2">
          Aún no hay medición: la primera foto se toma automáticamente la mañana siguiente a vincular el número.
        </p>
      </div>
    );
  }

  const sem = SEMAFORO[health.semaforo || 'rojo'];
  const p = health.parts || {};
  const resp = p['respuesta'];
  const bidi = p['bidireccional'];
  const vivos = p['chats_vivos'];
  const carga = p['carga_muda'];
  const frio = p['frio'];
  const mad = p['madurez'];
  const hig = p['higiene'];
  const r463 = p['r463'];

  // Orden = peso real en el score: respuesta (30) > ida y vuelta (20) > el resto (10 c/u).
  const rows: Array<Row | null> = [
    resp && (resp.medidos || 0) > 0 ? {
      icon: <MessageCircleReply size={16} />, label: 'Te responden', tip: resp.explica,
      value: `${resp.respondieron}/${resp.medidos} (${pct(resp.rate) ?? '—'})${resp.via_reaccion ? ` · ${resp.via_reaccion} con reacción` : ''}`,
    } : null,
    bidi && bidi.rate !== null && bidi.rate !== undefined ? {
      icon: <MessagesSquare size={16} />, label: 'Conversación de ida y vuelta', tip: bidi.explica,
      value: `${pct(bidi.rate)} (${bidi.vivos} de ${bidi.contrapartes})`,
    } : null,
    vivos ? {
      icon: <Users size={16} />, label: 'Personas que te escriben (90 días)', tip: vivos.explica,
      value: vivos.con_fuente ? `${vivos.vivos}` : 'sin captura aún',
    } : null,
    carga && carga.proactivos_30d !== undefined ? {
      icon: <BellOff size={16} />, label: 'Campañas y felicitaciones (30 días)', tip: carga.explica,
      value: `${carga.proactivos_30d} enviados${carga.sin_respuesta_est !== null && carga.sin_respuesta_est !== undefined && (resp?.medidos || 0) > 0 ? ` · sin respuesta ${carga.sin_respuesta_est} de ${resp!.medidos} medidos` : ' · respuesta aún sin medir'}`,
    } : null,
    frio && (frio.n || 0) >= 1 ? {
      icon: <Snowflake size={16} />, label: 'Envíos en frío (30 días)', tip: frio.explica,
      value: `${frio.frios} de ${frio.n}${frio.nuevos ? ` · ${frio.nuevos} a números nuevos` : ''}`,
    } : null,
    mad && mad.dias !== null && mad.dias !== undefined ? {
      icon: <CalendarClock size={16} />, label: 'Días desde la vinculación', tip: mad.explica, value: `${mad.dias}`,
    } : null,
    hig && (hig.envios_14d || 0) >= 10 ? {
      icon: <ShieldCheck size={16} />, label: 'Fallos de entrega (14 días)', tip: hig.explica,
      value: `${hig.fallos_14d} de ${hig.envios_14d}`,
    } : null,
    r463 && r463.dias !== null && r463.dias !== undefined ? {
      icon: <AlertTriangle size={16} />, label: 'Último bloqueo de WhatsApp (463)', tip: r463.explica,
      value: `hace ${r463.dias} días${r463.c30 ? ` · ${r463.c30} en 30 días` : ''}`,
    } : null,
  ];
  const visibles = rows.filter((r): r is Row => r !== null);
  const g = health.gate;

  return (
    <div className={`mt-5 space-y-4 ${cardCls}`} data-testid="wa-health">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-base font-bold text-content">
          <HeartPulse size={18} className="text-emerald-500" /> Salud del número
        </h2>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${sem.pill}`}>{sem.label}</span>
      </div>

      <div className="flex items-center gap-4">
        <div className="shrink-0 font-display text-3xl font-bold tabular-nums text-content">
          {health.score}<span className="text-base font-medium text-content-3">/100</span>
        </div>
        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-2">
          <div className={`h-full rounded-full ${sem.bar}`} style={{ width: `${health.score || 0}%` }} />
        </div>
      </div>

      {health.veredicto && <p className="text-sm text-content-2">{health.veredicto}</p>}

      {/* Gate anti-ban: qué restricción rige y hasta cuándo. */}
      {g?.activo && g.restriccion !== 'ninguna' && (() => {
        const todo = g.restriccion === 'todo';
        const cuando = g.hasta ? ` · se libera el ${fechaCorta(g.hasta)}`
          : g.motivo === 'sesion_caida' ? ' · hasta volver a vincular'
          : g.motivo === 'semaforo_rojo' ? ' · hasta que la salud mejore' : '';
        return (
          <div className={`rounded-xl px-3.5 py-2.5 ${todo ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
            <p className={`flex items-center gap-2 text-sm font-medium ${todo ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>
              {todo ? <ShieldAlert size={16} /> : <Shield size={16} />}
              {todo ? 'Envíos masivos en pausa automática' : 'Protección activa: campañas solo a quien ya ha conversado con el estudio'}
            </p>
            <p className="mt-0.5 text-xs text-content-2">Motivo: {g.label}{cuando}</p>
          </div>
        );
      })()}
      {(g?.rechazos?.length ?? 0) > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-content-3">
          <Shield size={12} />
          Protegido esta semana: {g!.rechazos!.reduce((a, r) => a + r.n, 0)} envíos frenados
          {' '}({g!.rechazos!.map((r) => `${r.n} ${r.label}`).join(' · ')})
        </p>
      )}

      {visibles.length > 0 && (
        <div className="grid gap-x-6 gap-y-2 pt-1 sm:grid-cols-2">
          {visibles.map((r, i) => (
            <div key={i}>
              <button type="button" onClick={() => r.tip && setOpenTip(openTip === i ? null : i)} title={r.tip}
                className="flex w-full items-center justify-between gap-2 text-left text-sm">
                <span className="flex items-center gap-2 text-content-2">{r.icon} {r.label}</span>
                <span className="text-right font-medium text-content">{r.value}</span>
              </button>
              {openTip === i && r.tip && <p className="mt-1 pl-6 text-xs text-content-3">{r.tip}</p>}
            </div>
          ))}
        </div>
      )}
      {visibles.some((r) => r.tip) && (
        <p className="-mt-1 text-[11px] text-content-3">Toca cualquier dato para ver qué significa con tus números.</p>
      )}

      <button onClick={() => setShowHelp(!showHelp)} className="flex items-center gap-1.5 text-xs font-medium text-content-2 hover:text-content">
        <HelpCircle size={14} /> ¿Cómo se calcula el score?
      </button>
      {showHelp && (
        <div className="space-y-1.5 rounded-xl bg-surface-2 px-3.5 py-2.5 text-xs text-content-2">
          <p>
            Mide qué tan «sano» ve WhatsApp a tu número. Lo que <b className="text-content">más pesa</b> es la conversación
            real: que la gente <b className="text-content">te responda</b> (30 pts) y que el chat sea de{' '}
            <b className="text-content">ida y vuelta</b> (20 pts). El resto: personas que te escriben, mensajes sin respuesta,
            envíos a desconocidos, antigüedad de la vinculación y fallos de entrega (10 pts cada uno).
          </p>
          <p>Solo se promedia lo que ya tiene datos suficientes — lo que aún no se puede medir <b className="text-content">no cuenta ni castiga</b>.</p>
          <p>
            <b className="text-emerald-700 dark:text-emerald-300">Verde (70+)</b>: puedes lanzar campañas ·{' '}
            <b className="text-amber-700 dark:text-amber-300">Ámbar (40-69)</b>: solo a quien conversó hace poco ·{' '}
            <b className="text-red-700 dark:text-red-300">Rojo (&lt;40)</b>: deja descansar el número.
          </p>
        </div>
      )}

      {health.measured_at && (
        <p className="text-[11px] text-content-3">
          Medido el {new Date(health.measured_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          {' '}· se recalcula cada mañana{health.stale ? ' · ⚠️ medición atrasada' : ''}
        </p>
      )}
    </div>
  );
}
